// Phase 0 Data Source Configuration Constants
// These will be replaced with dynamic data from Data Source Lambda in future phases

export const DATA_SOURCES = [
  { 
    sourceId: 'toast-pos', 
    name: 'Toast POS',
    description: 'Toast Point of Sale system integration',
    status: 'ACTIVE',
    itemType: 'stores',
    itemLable: 'Store Selection',
    defaultTimeConfig: {
      mode: 'Relative',
      relativeValue: 1,
      relativeUnit: 'DAY',
      granularity: 'DAILY'
    },
    supportedGranularities: ['DAILY', 'WEEKLY', 'MONTHLY']
  }
];

export const SOURCE_ENDPOINTS = [
  { 
    endpointId: 'orders', 
    name: 'Orders',
    description: 'Toast Orders API - retrieve order information and sales data',
    dataSourceId: 'toast-pos'
  },
  { 
    endpointId: 'labor', 
    name: 'Labor',
    description: 'Toast Labor API - retrieve labor information and labor data',
    dataSourceId: 'toast-pos'
  }
];

export const FILTER_DEFINITIONS = [
  {
    filterId: 'storeIds',
    endpointId: 'orders',
    name: 'storeIdsOrders',
    displayName: 'Store Name',
    description: 'Select one or more stores to include',
    valueType: 'ENUM',
    uiComponent: 'MULTISELECT',
    isRequired: true
  },
  {
    filterId: 'storeIds',
    endpointId: 'labor',
    name: 'storeIdsLabor',
    displayName: 'Store Name',
    description: 'Select one or more stores to include',
    valueType: 'ENUM',
    uiComponent: 'MULTISELECT',
    isRequired: true
  }
];

export const FILTER_VALUE_OPTIONS = [
  {
    optionId: 'storeId-clearfork',
    filterId: 'storeIds',
    value: 'clearfork',
    label: 'Clearfork'
  },
  {
    optionId: 'storeId-grapevine',
    filterId: 'storeIds',
    value: 'grapevine',
    label: 'Grapevine'
  },
  {
    optionId: 'storeId-palazzo',
    filterId: 'storeIds',
    value: 'palazzo',
    label: 'Palazzo'
  }
];

// Common time zones for easier selection
export const TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
];

// Schedule configuration helpers
export const SCHEDULE_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

export const DAYS_OF_WEEK = [
  { value: 'SUN', label: 'Sunday' },
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' }
];

// Helper function for creating cron expressions from user inputs
export const cronFromUserInputs = (frequency, days, hour, minute) => {
  if (frequency === 'daily') {
    return `0 ${minute} ${hour} * * ?`;
  } else if (frequency === 'weekly') {
    const daysList = days.join(',');
    return `0 ${minute} ${hour} ? * ${daysList}`;
  } else if (frequency === 'monthly') {
    // For a specific day of month
    return `0 ${minute} ${hour} ${days[0]} * ?`;
  }
  return `0 ${minute} ${hour} * * ?`; // Default daily fallback
};

// Helper function to parse cron expressions to user-friendly values
export const parseCronExpression = (cronExpression) => {
  if (!cronExpression) {
    return {
      frequency: 'daily',
      days: [],
      hour: 0,
      minute: 0
    };
  }
  
  try {
    const parts = cronExpression.split(' ');
    const minute = parseInt(parts[1], 10);
    const hour = parseInt(parts[2], 10);
    
    // Check if it's a daily schedule
    if (parts[3] === '*' && parts[4] === '*') {
      return {
        frequency: 'daily',
        days: [],
        hour,
        minute
      };
    }
    
    // Check if it's a weekly schedule
    if (parts[3] === '?' && parts[5] !== '*') {
      const days = parts[5].split(',');
      return {
        frequency: 'weekly',
        days,
        hour,
        minute
      };
    }
    
    // Check if it's a monthly schedule
    if (parts[4] === '*' && parts[3] !== '*' && parts[3] !== '?') {
      return {
        frequency: 'monthly',
        days: [parts[3]],
        hour,
        minute
      };
    }
    
    // Default fallback
    return {
      frequency: 'daily',
      days: [],
      hour,
      minute
    };
  } catch (err) {
    console.error('Error parsing cron expression:', err);
    return {
      frequency: 'daily',
      days: [],
      hour: 0,
      minute: 0
    };
  }
};

// Enum definitions that align with the GraphQL schema
export const ENUMS = {
  DataTimeMode: [
    { value: 'RELATIVE', label: 'Relative Time Range' },
    { value: 'ABSOLUTE', label: 'Specific Date Range' }
  ],
  
  ScheduleMode: [
    { value: 'RECURRING', label: 'Run on a Schedule' },
    { value: 'ONE_TIME', label: 'Run Once at Specific Time' },
  ],
  
  TimeZoneStrategy: [
    { value: 'ENTITY_LOCAL', label: 'Entity Local Time' },
    { value: 'SPECIFIC', label: 'Specific Time Zone' }
  ],
  
  TimeGranularity: [
    { value: 'HOURLY', label: 'Hourly' },
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' }
  ],
  
  TimeUnit: [
    { value: 'MINUTE', label: 'Minutes' },
    { value: 'HOUR', label: 'Hours' },
    { value: 'DAY', label: 'Days' },
    { value: 'WEEK', label: 'Weeks' },
    { value: 'MONTH', label: 'Months' }
  ],
  
  JobDefinitionStatus: [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'ARCHIVED', label: 'Archived' }
  ]
}